import { message } from 'antd'
import Form from 'antd/lib/form'
import { addEditBenefitReceivingMutation } from 'gql/client'
import { commonHelper } from 'helpers'
import { useState, useEffect } from 'react'
import { useGetCustomers, useGetReceivingBenefit } from 'services/clients/clients.service'
import { useCustomMutation } from 'services/shared/mutation'
import { AddEditBenefitReceivingPayload } from 'services/shared/mutation.payload'

import { commonUtils, eventManager } from 'utils'
import { getCustomersOptions } from 'utils/getCustomersOptions'

const initialState = {
  isOpen: false,
  data: { _id: '', isEdit: false },
  cb: { confirm: () => null, cancel: () => null },
  loading: false,
}

const useBenefitReceivingModal = () => {
  const [form] = Form.useForm()
  const addEditBenefitReceiving = useCustomMutation<AddEditBenefitReceivingPayload>({
    mutation: addEditBenefitReceivingMutation,
    refetchQueries: ['getBenefitsReceiving'],
  })
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const [modalState, setModalState] = useState(initialState)
  const receivingBenefitId = modalState?.data?._id
  const { receivingBenefit, loading: initialDataLoading } = useGetReceivingBenefit({
    idNumber,
    receivingBenefitId,
    skip: !modalState.isOpen || !modalState.data.isEdit,
  })
  const [loading, setLoading] = useState(initialDataLoading)
  const [formData, setFormData] = useState({
    benefitReceiver: receivingBenefit?.benefitReceiver,
    benefitType: receivingBenefit?.benefitType,
    benefitPeriod: receivingBenefit?.benefitPeriod,
    benefitAmount: receivingBenefit?.benefitAmount,
    isMeansBenefit: receivingBenefit?.isMeansBenefit,
    description: receivingBenefit?.description,
  })

  const { data: customersData } = useGetCustomers(idNumber)

  const customersOptions = getCustomersOptions(customersData, true)

  useEffect(() => {
    setLoading(initialDataLoading)
    const newData = commonUtils.removeTypeNameFromObject(receivingBenefit)
    delete newData._id
    setFormData({ ...newData })
    form.setFieldsValue({ ...newData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoading])

  const clearFormData = () => {
    setFormData((prevState) => commonUtils.resetFormData(prevState))
    form.setFieldsValue(commonUtils.resetFormData(formData))
  }

  const handleCustomSelectChange = (name: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleAntdFormInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  const isMeansBenefitChange = (checked: boolean) => {
    setFormData((prevState) => ({
      ...prevState,
      isMeansBenefit: checked,
    }))
  }

  const handleCustomTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  useEffect(() => {
    eventManager.addListener('receiving-benefit-modal', (data: any, cb: any) => {
      setModalState((state) => ({
        ...state,
        isOpen: data !== undefined,
        data,
        cb,
      }))
    })
  }, [setModalState])

  const handleClose = () => {
    setModalState((state) => ({ ...state, loading: false }))
    eventManager.closeBenefitReceivingModal()
  }

  const handleCancelClick = async () => {
    try {
      setModalState((state) => ({ ...state, loading: true }))
      if (modalState.cb.cancel) {
        await modalState.cb.cancel()
      }
    } catch (err) {
      // console.error(err)
    }
    handleClose()
    clearFormData()
  }

  const handleSubmit = async () => {
    const { errors } = await addEditBenefitReceiving({
      idNumber,
      benefitToEdit: receivingBenefitId,
      values: {
        ...formData,
        benefitAmount: Number(formData.benefitAmount),
      },
    })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
  }

  const handleConfirmClick = () => {
    form.validateFields().then(async (values) => {
      try {
        setModalState((state) => ({ ...state, loading: true }))
        await handleSubmit()
        clearFormData()
        if (modalState.cb.confirm) {
          await modalState.cb.confirm()
        }
      } catch (err) {
        // console.error(err)
      }
      setModalState((state) => ({ ...state, loading: false }))
      handleClose()
    })
  }

  return {
    modalState,
    handleCancelClick,
    handleConfirmClick,
    formData,
    handleCustomSelectChange,
    handleAntdFormInputChange,
    isMeansBenefitChange,
    handleCustomTextAreaChange,
    loading,
    form,
    customersOptions,
  }
}

export default useBenefitReceivingModal
